import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Percent, Truck, ChevronRight, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import { customerVisibleCoupons } from '../../utils/couponForm';

const OFFER_ICONS = {
  percent_discount: Percent,
  flat_discount: Tag,
  free_delivery: Truck,
};

const OFFER_LABELS = {
  percent_discount: (c) => `${c.discount_value}% OFF`,
  flat_discount: (c) => `FLAT ${c.currency || '\u20B9'}${c.discount_value} OFF`,
  free_delivery: () => 'FREE DELIVERY',
};

export const CouponStrip = ({ coupons: allCoupons, variant = 'default', currencySymbol = '\u20B9' }) => {
  const coupons = customerVisibleCoupons(allCoupons);
  if (coupons.length === 0) return null;

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success(`Code ${code} copied!`);
    }).catch(() => {});
  };

  const themeColors = {
    default: { bg: 'bg-gradient-to-r from-amber-50 to-orange-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-600', accent: 'text-amber-600' },
    instamart: { bg: 'bg-gradient-to-r from-green-50 to-emerald-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-[#0C831F]', accent: 'text-[#0C831F]' },
    zepto: { bg: 'bg-gradient-to-r from-purple-50 to-violet-50', border: 'border-purple-200', text: 'text-purple-800', badge: 'bg-[#5B2EBF]', accent: 'text-[#5B2EBF]' },
    blinkit: { bg: 'bg-gradient-to-r from-yellow-50 to-amber-50', border: 'border-yellow-300', text: 'text-yellow-900', badge: 'bg-[#E8A318]', accent: 'text-[#C99215]' },
    country: { bg: 'bg-gradient-to-r from-green-50 to-teal-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-[#00B37A]', accent: 'text-[#00B37A]' },
  };

  const t = themeColors[variant] || themeColors.default;

  return (
    <div className="mb-4" data-testid="coupon-strip">
      <div className="flex items-center justify-between px-4 mb-2">
        <h3 className="font-bold text-sm text-[var(--theme-text-main)] flex items-center gap-1.5">
          <Tag className="w-4 h-4" /> Offers For You
        </h3>
        <Link to="/cart" className="text-xs text-[var(--theme-primary)] font-medium flex items-center gap-0.5">
          Apply at cart <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-2 snap-x" style={{ scrollbarWidth: 'none' }}>
        {coupons.slice(0, 8).map((coupon, i) => {
          const Icon = OFFER_ICONS[coupon.offer_type] || Tag;
          const labelFn = OFFER_LABELS[coupon.offer_type];
          const label = labelFn ? labelFn({ ...coupon, currency: currencySymbol }) : coupon.code;

          return (
            <div
              key={coupon.id || i}
              className={`snap-start shrink-0 w-56 ${t.bg} border ${t.border} rounded-xl p-3 relative overflow-hidden`}
              data-testid={`coupon-card-${i}`}
            >
              {/* Dashed cut line */}
              <div className="absolute top-0 bottom-0 left-12 border-l-2 border-dashed border-gray-300/50" />
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${t.badge} rounded-lg flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`font-extrabold text-xs ${t.text} tracking-wide`}>{label}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5 truncate">{coupon.title || coupon.description}</p>
                  {coupon.min_order_value > 0 && (
                    <p className="text-[10px] text-gray-400 mt-0.5">Min order {currencySymbol}{coupon.min_order_value}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => copyCode(coupon.code)}
                className={`mt-2 w-full flex items-center justify-center gap-1 border border-dashed ${t.border} rounded-lg py-1 text-[10px] font-bold ${t.accent} hover:bg-white/50 transition-colors`}
                data-testid={`copy-coupon-${i}`}
              >
                <Scissors className="w-3 h-3" /> {coupon.code}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
